import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { getLineItemDisplay } from '@/lib/line-item-display'
import type { LineItemSource } from '@/lib/services/line-items/line-items.types'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'

function DetailSection({ children, label }: { children: ReactNode; label: string }) {
  return (
    <section className="space-y-2">
      <Typography variant="labelStrong" className="block">
        {label}
      </Typography>
      <div className="text-sm leading-6">{children}</div>
    </section>
  )
}

function ViewItemDetails({ item }: { item: LineItemSource | null }) {
  const display = getLineItemDisplay(item)
  const notes = display.notes
  const recipe = display.recipe
  const path = usePathHelper()
  const linkedProductId = display.productId && !display.isCustom ? display.productId : null

  return (
    <div className="px-5 pt-8 pb-8 sm:px-6 sm:pt-9">
      <Typography variant="h3" className="pr-10">
        Item details
      </Typography>

      <div className="mt-7 space-y-6">
        <div className="space-y-1">
          <Typography variant="h4" className="whitespace-pre-line">
            {display.primaryName}
          </Typography>
          {display.badgeLabel && (
            <Typography variant="supporting" className="block">
              {display.badgeLabel}
            </Typography>
          )}
        </div>

        {notes ? (
          <DetailSection label="Notes">
            <Typography variant="body" className="whitespace-pre-line">
              {notes}
            </Typography>
          </DetailSection>
        ) : null}

        <DetailSection label="Recipe">
          {recipe ? (
            <ul className="flex flex-col gap-y-1">
              {recipe.split('\n').map((line, index) => (
                // Florists line recipes up as grids with runs of spaces.
                <li key={index} className="whitespace-pre-wrap">
                  {line}
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex flex-col gap-y-2">
              <Typography variant="supporting">
                No recipe available for this item at the time of purchase.
              </Typography>
              {linkedProductId !== null && (
                <Typography variant="supporting">
                  You can add a recipe to each variant on{' '}
                  <Typography variant="supporting" tone="interaction" interaction="link">
                    <Link {...path.products.show(linkedProductId).link}>the product page</Link>
                  </Typography>
                </Typography>
              )}
            </div>
          )}
        </DetailSection>

        {display.description ? (
          <DetailSection label="Description">
            <Typography variant="body" className="whitespace-pre-line">
              {display.description}
            </Typography>
          </DetailSection>
        ) : null}
      </div>
    </div>
  )
}

export default ViewItemDetails
